import { ReactElement, useMemo, useCallback } from 'react';
import { Typography, Link } from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserTie as DriverIcon,
  faGauge as SpeedIcon,
  faBatteryThreeQuarters as BatteryIcon,
} from '@fortawesome/free-solid-svg-icons';
import { isUndefined, isNull, round } from 'lodash';
import moment from 'moment-timezone';

import { useUtcOffset } from '~hooks/index';

import { VehiclePopupMapProps } from './types';
import {
  VehiclePopupMapContainer,
  VehiclePopupMapTitle,
  VehiclePopupMapRowItem,
  VehiclePopupMapIconContainer,
} from './styles';

const VehiclePopupMap = ({
  vehicle,
  driver,
}: VehiclePopupMapProps): ReactElement => {
  const formatterDateUtcOffset = useUtcOffset();

  const title = useMemo(() => {
    let output = vehicle.name;

    if (vehicle.referenceCode) {
      output += ` (${vehicle.referenceCode})`;
    }

    return output;
  }, [vehicle]);

  const getLastUpatedInfo = useCallback(
    (lastDate: string) => {
      const currentDate = moment();
      const lastUpdatedDate = formatterDateUtcOffset(
        lastDate,
        moment.HTML5_FMT.DATETIME_LOCAL,
      );

      const diffInSeconds = currentDate.diff(lastUpdatedDate, 'seconds');

      const timeDuration = moment.duration(diffInSeconds, 'seconds');

      return `Actualizado hace ${timeDuration.humanize()}`;
    },
    [formatterDateUtcOffset],
  );

  const contactNumber = useMemo(
    () => driver.contactMobile ?? driver.contactPhone,
    [driver.contactMobile, driver.contactPhone],
  );

  return (
    <VehiclePopupMapContainer>
      <VehiclePopupMapTitle>{title}</VehiclePopupMapTitle>

      {!isUndefined(vehicle.speed) && !isNull(vehicle.speed) && (
        <VehiclePopupMapRowItem>
          <VehiclePopupMapIconContainer>
            <FontAwesomeIcon icon={SpeedIcon} />
          </VehiclePopupMapIconContainer>

          <Typography variant="inherit">
            {`${round(vehicle.speed)} KM/h`}
          </Typography>
        </VehiclePopupMapRowItem>
      )}

      <VehiclePopupMapRowItem>
        <VehiclePopupMapIconContainer>
          <FontAwesomeIcon icon={DriverIcon} />
        </VehiclePopupMapIconContainer>

        <Typography variant="inherit">{driver.name}</Typography>
      </VehiclePopupMapRowItem>

      {contactNumber && (
        <VehiclePopupMapRowItem>
          <VehiclePopupMapIconContainer>
            <WhatsAppIcon
              sx={(currenTheme) => ({
                fontSize: currenTheme.typography.pxToRem(14),
              })}
            />
          </VehiclePopupMapIconContainer>

          <Link
            href={`https://wa.me/${contactNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="inherit"
            color="inherit"
            underline="always"
          >
            {contactNumber}
          </Link>
        </VehiclePopupMapRowItem>
      )}

      {!isUndefined(driver.battery) && !isNull(driver.battery) && (
        <VehiclePopupMapRowItem>
          <VehiclePopupMapIconContainer>
            <FontAwesomeIcon icon={BatteryIcon} />
          </VehiclePopupMapIconContainer>

          <Typography variant="inherit">
            {`${round(driver.battery * 100)} %`}
          </Typography>
        </VehiclePopupMapRowItem>
      )}

      {driver.lastGeoReferenceDateTime && (
        <VehiclePopupMapRowItem>
          <Typography variant="inherit" color="text.disabled">
            {getLastUpatedInfo(driver.lastGeoReferenceDateTime)}
          </Typography>
        </VehiclePopupMapRowItem>
      )}
    </VehiclePopupMapContainer>
  );
};

export default VehiclePopupMap;
